import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Upload, RefreshCw, X, Paperclip, Image as ImageIcon, Film, Download } from 'lucide-react';
import { Label } from '@/components/ui/label';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MAX_SIZE = 50 * 1024 * 1024; // 50MB
const VIDEO_EXTS = ['mp4', 'mov', 'webm', 'avi', 'mkv', 'm4v'];

const formatSize = (bytes) => {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
};

const isVideo = (name = '') => VIDEO_EXTS.includes(name.split('.').pop()?.toLowerCase());

/**
 * Reusable photo/video uploader. `attachments` is an array of
 * { id, filename, size } objects; `setAttachments` updates it.
 */
export const MediaUploader = ({ attachments = [], setAttachments, disabled = false, label = 'Photos / Videos', compact = false }) => {
  const [uploading, setUploading] = useState(false);
  const token = localStorage.getItem('token');

  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > MAX_SIZE) {
      toast.error('File too large. Maximum size is 50MB');
      e.target.value = '';
      return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('category', 'report');
      const response = await axios.post(`${API}/files/upload?category=report`, formData, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
      });
      setAttachments([...attachments, {
        id: response.data.id,
        filename: response.data.filename,
        size: response.data.size
      }]);
      toast.success('Media attached');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload media');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const removeAttachment = (fileId) => setAttachments(attachments.filter(a => a.id !== fileId));

  return (
    <div className="space-y-2">
      {label && <Label className="text-xs text-slate-500">{label}</Label>}
      <label className={`flex items-center justify-center gap-2 ${compact ? 'p-2' : 'p-3'} border-2 border-dashed rounded-lg cursor-pointer hover:bg-slate-50 transition-colors ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}>
        <input
          type="file"
          className="hidden"
          onChange={handleFileSelect}
          accept=".jpg,.jpeg,.png,.gif,.webp,.mp4,.mov,.webm,.avi,.mkv,.m4v"
          disabled={disabled || uploading}
          data-testid="media-upload-input"
        />
        {uploading ? <RefreshCw className="w-4 h-4 animate-spin text-slate-400" /> : <Upload className="w-4 h-4 text-slate-400" />}
        <span className="text-xs text-slate-500">{uploading ? 'Uploading...' : 'Attach photo / video'}</span>
      </label>
      {attachments.length > 0 && (
        <div className="space-y-1">
          {attachments.map((att) => (
            <div key={att.id} className="flex items-center gap-2 p-2 bg-slate-50 rounded-lg text-sm">
              {isVideo(att.filename) ? <Film className="w-4 h-4 text-purple-500 flex-shrink-0" /> : <ImageIcon className="w-4 h-4 text-blue-500 flex-shrink-0" />}
              <span className="flex-1 truncate">{att.filename}</span>
              {att.size ? <span className="text-xs text-slate-400">{formatSize(att.size)}</span> : null}
              {!disabled && (
                <button type="button" onClick={() => removeAttachment(att.id)} className="text-red-500 hover:text-red-700" data-testid={`remove-media-${att.id}`}>
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

/** Read-only viewer: renders inline image/video previews with download links. */
export const MediaViewer = ({ attachments = [] }) => {
  const token = localStorage.getItem('token');
  if (!attachments || attachments.length === 0) return null;
  return (
    <div className="mt-2 grid grid-cols-2 sm:grid-cols-3 gap-2">
      {attachments.map((att, idx) => {
        const url = `${API}/files/${att.id}/download?auth=${token}`;
        const video = isVideo(att.filename);
        return (
          <div key={att.id || idx} className="relative rounded-lg overflow-hidden border bg-slate-50">
            {video ? (
              <video src={url} controls className="w-full h-28 object-cover bg-black" />
            ) : (
              <img src={url} alt={att.filename || `attachment-${idx}`} className="w-full h-28 object-cover" />
            )}
            <a
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              className="absolute bottom-1 right-1 bg-white/90 rounded p-1 shadow hover:bg-white"
              title="Open / download"
            >
              <Download className="w-3.5 h-3.5 text-slate-700" />
            </a>
          </div>
        );
      })}
    </div>
  );
};

export default MediaUploader;
